import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

// One shared copy of the organization's plan, so every notice on a screen doesn't fetch it again.
let cached = null;
let cachedAt = 0;
let inflight = null;
const listeners = new Set();
const FRESH_MS = 60 * 1000;

function load(force) {
  if (!force && cached && Date.now() - cachedAt < FRESH_MS) return Promise.resolve(cached);
  if (!inflight) {
    inflight = axios.get('/api/organizations/subscription')
      .then((r) => { cached = r.data; cachedAt = Date.now(); return cached; })
      .catch(() => cached)
      .finally(() => { inflight = null; listeners.forEach((l) => l(cached)); });
  }
  return inflight;
}

/** Forget the cached plan, e.g. at sign-out or after the plan changes. */
export function resetPlanCache() {
  cached = null;
  cachedAt = 0;
}

/**
 * The organization's plan: which features it includes, the plan name, and this month's document usage.
 * While it is loading, features count as included so a screen never flashes a "not in your plan" notice.
 */
export default function usePlan() {
  const [data, setData] = useState(cached);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    let active = true;
    const listener = (d) => { if (active) setData(d); };
    listeners.add(listener);
    load(false).then((d) => { if (active) { setData(d); setLoading(false); } });
    return () => { active = false; listeners.delete(listener); };
  }, []);

  const features = new Set(data?.features || []);
  const catalog = data?.featureCatalog || [];
  const labelOf = (key) => catalog.find((f) => f.key === key)?.label || key;

  return {
    loading,
    plan: data?.subscriptionPlan || null,
    usage: data?.usage || null,
    has: (key) => loading || !data || features.has(key),
    label: labelOf,
    upgradeHint: (key) => data?.upgradeHints?.[key] || null,
    reload: useCallback(() => load(true).then((d) => setData(d)), [])
  };
}
